/**
 * useConversations Hook - Manages conversation history with localStorage persistence
 */

import { useState, useEffect, useCallback } from 'react';
import type { Message } from '@bsp-chatbot/shared';

export interface StoredConversation {
  id: string;
  title: string;
  messages: Message[];
  createdAt: string;
  updatedAt: string;
}

const STORAGE_KEY = 'bsp-chatbot-conversations';
const MAX_CONVERSATIONS = 50;

function loadConversations(): StoredConversation[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load conversations:', e);
  }
  return [];
}

function saveConversations(conversations: StoredConversation[]): void {
  try {
    // Keep only the most recent conversations
    const trimmed = conversations.slice(0, MAX_CONVERSATIONS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.error('Failed to save conversations:', e);
  }
}

export function useConversations() {
  const [conversations, setConversations] = useState<StoredConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

  // Load conversations on mount
  useEffect(() => {
    setConversations(loadConversations());
  }, []);

  // Get active conversation
  const activeConversation = conversations.find(c => c.id === activeConversationId);

  // Create new conversation (does NOT set it as active - caller should do that after streaming)
  const createConversation = useCallback((): string => {
    const newConversation: StoredConversation = {
      id: crypto.randomUUID(),
      title: 'New Chat',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setConversations(prev => {
      const updated = [newConversation, ...prev];
      saveConversations(updated);
      return updated;
    });

    // DON'T set active conversation here - it changes the key prop and causes React to unmount/remount
    // The ChatInterface will track it locally and the parent will see it when messages are added
    return newConversation.id;
  }, []);

  // Add message to conversation (optionally set as active)
  const addMessage = useCallback((conversationId: string, message: Message, setActive: boolean = false): void => {
    // Only set as active if explicitly requested (typically after streaming completes)
    if (setActive) {
      setActiveConversationId(conversationId);
    }

    setConversations(prev => {
      const updated = prev.map(conv => {
        if (conv.id === conversationId) {
          const messages = [...conv.messages, message];
          // Update title from first user message
          const title = conv.title === 'New Chat' && message.role === 'user'
            ? message.content.slice(0, 40) + (message.content.length > 40 ? '...' : '')
            : conv.title;
          return {
            ...conv,
            messages,
            title,
            updatedAt: new Date().toISOString(),
          };
        }
        return conv;
      });

      // Move updated conversation to top
      const convIndex = updated.findIndex(c => c.id === conversationId);
      if (convIndex > 0) {
        const [conv] = updated.splice(convIndex, 1);
        updated.unshift(conv);
      }

      saveConversations(updated);
      return updated;
    });
  }, []);

  // Update message in conversation (for streaming)
  const updateMessage = useCallback((conversationId: string, messageId: string, updates: Partial<Message>): void => {
    setConversations(prev => {
      const updated = prev.map(conv => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            messages: conv.messages.map(msg =>
              msg.id === messageId ? { ...msg, ...updates } : msg
            ),
            updatedAt: new Date().toISOString(),
          };
        }
        return conv;
      });
      saveConversations(updated);
      return updated;
    });
  }, []);

  // Delete conversation
  const deleteConversation = useCallback((conversationId: string): void => {
    setConversations(prev => {
      const updated = prev.filter(c => c.id !== conversationId);
      saveConversations(updated);
      return updated;
    });

    if (activeConversationId === conversationId) {
      setActiveConversationId(null);
    }
  }, [activeConversationId]);

  // Select conversation
  const selectConversation = useCallback((conversationId: string | null): void => {
    setActiveConversationId(conversationId);
  }, []);

  // Start new chat
  const startNewChat = useCallback((): void => {
    setActiveConversationId(null);
  }, []);

  return {
    conversations,
    activeConversation,
    activeConversationId,
    createConversation,
    addMessage,
    updateMessage,
    deleteConversation,
    selectConversation,
    startNewChat,
  };
}
